import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, Lock } from "lucide-solid";
import type { ShopProduct } from "@famidex/powersync";
import { canAffordRedemption } from "@famidex/shared";

import { useI18n } from "@/providers/i18n-provider";
import { AcornGlyph, formatAcorns, ShopIcon } from "./shop-visuals";

/**
 * Tienda de CANJE con bellotas: marcos (`canje_de_recompensas`) y stickers
 * (`tienda_de_stickers`). Genérica sobre la familia. El servidor revalida saldo y
 * unicidad; aquí sólo apagamos el botón cuando no alcanza (canAffordRedemption,
 * pura) o cuando el ítem ya está canjeado. El descuento lo aplica el servidor.
 */
export function RedemptionShop(props: {
  title: string;
  testid: string;
  items: ShopProduct[];
  balance: number;
  redeemedIds: Set<string>;
  busy: boolean;
  onRedeem: (product: ShopProduct) => void;
}) {
  const { t } = useI18n();

  return (
    <Show when={props.items.length > 0}>
      <section class="flex flex-col gap-3" data-testid={props.testid}>
        <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {props.title}
        </h2>
        <ul class="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <For each={props.items}>
            {(item) => {
              const redeemed = () => props.redeemedIds.has(item.id);
              const price = () => item.price_acorns ?? 0;
              const affordable = () => canAffordRedemption(props.balance, price());
              return (
                <li
                  data-testid="redeem-item"
                  class="flex flex-col items-center gap-2 rounded-xl border border-border/60 bg-card p-3 text-center"
                >
                  <span
                    class="grid h-14 w-14 place-items-center rounded-2xl bg-accent text-accent-foreground"
                    classList={{ "opacity-50": redeemed() }}
                  >
                    <ShopIcon name={item.icon} class="h-7 w-7" />
                  </span>
                  <h3 class="line-clamp-2 text-xs font-semibold leading-tight text-foreground">
                    {item.name}
                  </h3>
                  <Show
                    when={!redeemed()}
                    fallback={
                      <span class="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold text-primary">
                        <Check class="h-3.5 w-3.5" />
                        {t(msg`Obtenido`)}
                      </span>
                    }
                  >
                    <button
                      type="button"
                      data-testid="redeem-button"
                      disabled={props.busy || !affordable()}
                      onClick={() => {
                        props.onRedeem(item);
                      }}
                      class="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Show when={affordable()} fallback={<Lock class="h-3 w-3" />}>
                        <AcornGlyph class="h-3 w-3" />
                      </Show>
                      {formatAcorns(price())}
                    </button>
                  </Show>
                </li>
              );
            }}
          </For>
        </ul>
      </section>
    </Show>
  );
}
